import { useId } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { PageLayoutDesktop } from "src/components/layout/PageLayoutDesktop";
import { PageLayoutMobile } from "src/components/layout/PageLayoutMobile";
import { StatTile } from "./components/StatTile";
import { legacyMetricsSummaryQueryOptions } from "./legacyMetrics.queries";

/**
 * Legacy Metrics home (Figma "Customer Portal Designs" > Legacy Metrics >
 * "Legacy Metrics Home"): the Top Level Stats card across all partners.
 * The Saved Search Stats and MediaView Stats screens are follow-ups.
 */
export function LegacyMetricsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const summary = useQuery(legacyMetricsSummaryQueryOptions());
  const statsHeadingId = useId();

  const title = (
    <Typography variant="h5" component="h1">
      Legacy Metrics
    </Typography>
  );

  const body = (
    <>
      {summary.isPending && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress aria-label="Loading legacy metrics" />
        </Box>
      )}

      {summary.isError && (
        <Alert severity="error">
          Couldn&apos;t load legacy metrics
          {summary.error.message ? `: ${summary.error.message}` : "."}
        </Alert>
      )}

      {summary.isSuccess && (
        <Paper component="section" aria-labelledby={statsHeadingId} sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography id={statsHeadingId} variant="h6" component="h2">
              Top Level Stats
            </Typography>
            {/* Neutral chip, focusable so its tooltip is reachable by keyboard. Its
                text is text.primary on paper: 15.7:1 light, 12.8:1 dark (elevation-2
                paper is about rgb(50,50,50)). warning.main text fails AA in light
                mode (3.11:1 on white; warning.dark 3.79:1), so the words and the
                icon carry the meaning, not color. */}
            {summary.data.isSample && (
              <Tooltip title="Placeholder values from the design. Real figures arrive once their data sources are agreed.">
                <Chip
                  label="Sample data"
                  size="small"
                  variant="outlined"
                  icon={<InfoOutlined aria-hidden />}
                  tabIndex={0}
                />
              </Tooltip>
            )}
          </Box>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Here&apos;s what&apos;s been happening for all partners over the last 30 days
          </Typography>
          <Box
            sx={{
              display: "grid",
              mt: 1,
              columnGap: 2,
              rowGap: 1,
              p: 2,
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(5, minmax(0, 1fr))",
              },
            }}
          >
            {summary.data.topLevelStats.map((stat) => (
              <StatTile
                key={stat.key}
                label={stat.label}
                value={stat.value}
                caption={stat.caption}
              />
            ))}
          </Box>
        </Paper>
      )}
    </>
  );

  if (isMobile) {
    return (
      <PageLayoutMobile
        content={
          <>
            {title}
            {body}
          </>
        }
      />
    );
  }

  return <PageLayoutDesktop header={title} content={body} />;
}
